<script setup>
  import { storeToRefs } from "pinia";
  import { computed, defineAsyncComponent, shallowRef, watch } from "vue";
  import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
  import { pascalCase, capitalCase } from "change-case";
  import { Info } from "lucide-vue-next";
  import { Tooltip } from "@/components/ui/tooltip";

  const components = shallowRef({});
  const currentCardStore = useCurrentCardStore();
  const { settings, isSettingHidden } = storeToRefs(currentCardStore);
  const determineComponent = (name) => defineAsyncComponent(() => import(`./${pascalCase(name)}Setting.vue`));
  const determineComponents = () => {
    components.value = {};
    Object.keys(settings.value).forEach((settingName) => {
      components.value[settingName] = determineComponent(settingName);
    });
  };
  const additionalProps = (name, { label, ...value }) => ({
    ...value,
    label: label || capitalCase(name),
  });

  watch(settings, () => determineComponents(), { immediate: true });

  const SETTING_GROUPS = [
    {
      label: "Answer options",
      names: [
        "skippable",
        "isOther",
        "exclusiveAnswers",
        "randomizeAnswers",
        "randomizeItems",
        "dynamicAnswers",
        "dynamicItems",
        "limits",
        "itemLimits",
        "numericLimits",
        "sliderLimits",
        "allowDecimals",
        "rating",
        "responseFormat",
        "qMatrix",
        "qCity",
        "multipleMedia",
        "buttons",
        "consent",
        "translatable",
      ],
    },
    {
      label: "Display",
      names: [
        "compactLayout",
        "dropdownInput",
        "displayImageNames",
        "hasHint",
        "viewOnce",
        "viewOnceDuration",
        "typing",
        "qr",
      ],
    },
    {
      label: "Logic",
      names: ["screenout", "answerConditions", "itemConditions", "randomizeCard", "randomizeLoops", "isPii"],
    },
  ];

  const groupedSettings = computed(() => {
    const visible = Object.entries(settings.value).filter(([name]) => !isSettingHidden.value[name]);
    const known = new Set(SETTING_GROUPS.flatMap((group) => group.names));
    const groups = SETTING_GROUPS.map((group) => ({
      label: group.label,
      entries: group.names.map((name) => visible.find(([key]) => key === name)).filter(Boolean),
    }));
    groups.push({ label: "Other", entries: visible.filter(([name]) => !known.has(name)) });
    const filled = groups.filter((group) => group.entries.length > 0);

    if (filled.length && filled.every((group) => group.entries.length < 2)) {
      return [{ label: "Options", entries: filled.flatMap((group) => group.entries) }];
    }
    return filled;
  });
</script>

<template>
  <div class="flex-1 space-y-4 overflow-y-auto px-1">
    <div
      v-for="group in groupedSettings"
      :key="group.label"
      class="space-y-2.5">
      <p class="heading-label">{{ group.label }}</p>
      <div
        v-for="[name, value] in group.entries"
        :key="`current-card-body-setting-${name}`"
        class="group flex items-center justify-between">
        <div class="flex-1">
          <component
            :is="components[name]"
            v-bind="additionalProps(name, value)" />
        </div>
        <Tooltip
          v-if="value.hint"
          :content="value.hint">
          <Info
            class="ml-2 h-3.5 w-3.5 shrink-0 cursor-help text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </Tooltip>
      </div>
    </div>
  </div>
</template>
